// import React, { useContext } from 'react';
// // import jwtDecode from 'jwt-decode';
// import useAuth from '../hooks/useAuth';

// Endpoint classification for login state management
const AUTHENTICATED_ENDPOINTS = [
    '/api/v1/login',
    '/api/v1/logout', 
    '/api/v1/refresh-token',
    '/api/v1/post/add',
    '/api/v1/post/like',
    '/api/v1/post/comment/add',
    '/api/v1/post/comment/like',
    '/api/v1/channel/create',
    '/api/v1/channel/message/add',
    // '/api/v1/user/profile',
    '/api/v1/user/update',
    '/api/v1/user/profile-image/upload',
    '/api/v1/user/profile-image',
    '/api/v1/discussion',
    '/api/v1/discussion/list',
    '/api/v1/discussion/like',
    '/api/v1/content/*/comments'
];

const PUBLIC_ENDPOINTS = [
    '/api/v1/signup',
    '/api/v1/login',
    // '/api/v1/logout',
    '/api/v1/post/list',
    '/api/v1/post/',
    '/api/v1/post/comment/list',
    '/api/v1/reset-password',
    '/api/v1/user-activation'
];

const isAuthenticatedEndpoint = (url) => {
    return AUTHENTICATED_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

const isPublicEndpoint = (url) => {
    return PUBLIC_ENDPOINTS.some(endpoint => url.includes(endpoint));
};

const isLogoutEndpoint = (url) => {
    return url.includes('/api/v1/logout');
};

// Enhanced error handling with detailed error information
const handleApiError = (response, url) => {
    const error = new Error(`API call failed: ${response.status} ${response.statusText}`);
    error.response = response;
    error.url = url;
    error.status = response.status;
    
    // Log error details for debugging
    console.error('API Error:', {
        url,
        status: response.status,
        statusText: response.statusText,
        timestamp: new Date().toISOString()
    });
    
    return error;
};

const apiCall = async (url, method, body = {}, headers = {}) => {
    // For web clients, authentication is handled via HTTP-only cookies
    // No need for manual token management
    
    const config = {
        method,
        credentials: 'include',
        headers: {
            'X-Client-Type': 'web',
            ...headers
        }
    };

    // Handle FormData separately - don't set Content-Type header
    if (body instanceof FormData) {
        config.body = body;
        // Remove Content-Type header - browser will set it with boundary
        delete config.headers['Content-Type'];
    } else if (method !== 'GET' && body) {
        // For JSON data, set Content-Type and stringify
        config.headers['Content-Type'] = 'application/json';
        config.body = JSON.stringify(body);
    }

    // Handle relative URLs (for proxy setup in development)
    const fullUrl = url.startsWith('http') ? url : url;

    // Check and refresh token before making authenticated requests
    if (!isPublicEndpoint(url) && !isLogoutEndpoint(url)) {
        // Import the token expiration check function
        const { isStoredTokenAboutToExpire } = await import('./jwtUtils');
        
        if (isStoredTokenAboutToExpire()) {
            console.log('Token refresh needed before API call');
            try {
                // Import the silent refresh function from authUtils
                const { silentRefreshWithBackoff } = await import('../utils/authUtils');
                
                // We need to create a refreshSession function to pass to silentRefreshWithBackoff
                // Use the same approach as the main apiCall function - relative URL for proxy
                const refreshSession = async () => {
                    // Get API base URL using the same pattern as the rest of the application
                    // Import the utility function dynamically to avoid circular dependencies
                    const { getApiBaseUrl } = await import('./getApiBaseUrl');
                    const apiBase = getApiBaseUrl();
                    
                    // Construct the URL the same way as useAuth.js does
                    const refreshUrl = apiBase ? `${apiBase}/api/v1/refresh-token` : '/api/v1/refresh-token';
                    
                    // Use the same fetch configuration as the main apiCall function
                    const config = {
                        method: 'POST',
                        credentials: 'include',
                        headers: {
                            'Content-Type': 'application/json',
                            'X-Client-Type': 'web'
                        }
                    };
                    
                    const response = await fetch(refreshUrl, config);
                    
                    if (!response.ok) {
                        const errorData = await response.json().catch(() => ({}));
                        const errorMessage = errorData.message || 'Session refresh failed';
                        throw new Error(errorMessage);
                    }
                    
                    const data = await response.json();
                    return { success: true, ...data };
                };
                
                // Call the silent refresh with our refreshSession function
                await silentRefreshWithBackoff(0, refreshSession);
            } catch (error) {
                console.error('Token refresh failed before API call:', error);
                // Continue with original request, let 401 handling take over
            }
        }
    }

    try {
        const response = await fetch(fullUrl, config);

        // Handle successful responses - update login state based on endpoint type
        if (response.ok && isAuthenticatedEndpoint(url)) {
            if (isLogoutEndpoint(url)) {
                localStorage.setItem('isLoggedIn', 'false');
            } else {
                localStorage.setItem('isLoggedIn', 'true');
            }
        }
        // Handle 401 Unauthorized - clear login state and redirect
        else if (response.status === 401) {
            if (localStorage.getItem('isDemoMode') !== 'true') {
                localStorage.setItem('isLoggedIn', 'false');
                if (!isPublicEndpoint(url)) {
                    // Only redirect for non-public endpoints
                    window.location.href = '/login';
                }
            }
        }
        // Handle 500 Internal Server Error - this might be due to device headers issue
        else if (response.status === 500) {
            console.error('Server error - this might be due to device headers compatibility issue');
            // Try to extract error details if available
            try {
                const errorData = await response.json();
                console.error('Server error details:', errorData);
                throw handleApiError(response, url);
            } catch (jsonError) {
                // If we can't parse JSON, just throw the original error
                throw handleApiError(response, url);
            }
        }
        // Handle other error statuses
        else if (!response.ok) {
            throw handleApiError(response, url);
        }

        return response;
    } catch (error) {
        // Handle network errors and other exceptions
        console.error('Network/API Error:', error);
        
        // For network errors, clear login state and redirect if not a public endpoint
        if (error.name === 'TypeError' && !isPublicEndpoint(url) && localStorage.getItem('isDemoMode') !== 'true') {
            localStorage.setItem('isLoggedIn', 'false');
            window.location.href = '/login';
        }
        
        // Provide more user-friendly error messages for common issues
        if (error.message && error.message.includes('500')) {
            throw new Error('Server error occurred. Please try again later.');
        }
        
        throw error;
    }
};

// Utility function to check if user is authenticated
const isUserAuthenticated = () => {
    return localStorage.getItem('isLoggedIn') === 'true';
};

/**
 * Specialized function for uploading profile images with progress tracking
 * @param {string} url - API endpoint URL
 * @param {File} file - Image file to upload
 * @param {Function} onProgress - Progress callback (optional)
 * @returns {Promise<Response>} - API response
 */
const uploadProfileImage = async (url, file, onProgress) => {
    const formData = new FormData();
    formData.append('image', file);
    
    const config = {
        method: 'POST',
        credentials: 'include',
        body: formData,
        headers: {
            'X-Client-Type': 'web'
        }
    };
    
    // Add progress tracking if callback provided
    if (onProgress && typeof onProgress === 'function') {
        const xhr = new XMLHttpRequest();
        
        xhr.open('POST', url, true);
        xhr.withCredentials = true;
        xhr.setRequestHeader('X-Client-Type', 'web');
        
        xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
                const percentComplete = (event.loaded / event.total) * 100;
                onProgress(percentComplete);
            }
        };
        
        return new Promise((resolve, reject) => {
            xhr.onload = () => {
                if (xhr.status >= 200 && xhr.status < 300) {
                    try {
                        const response = {
                            ok: true,
                            status: xhr.status,
                            statusText: xhr.statusText,
                            json: () => JSON.parse(xhr.responseText)
                        };
                        resolve(response);
                    } catch (error) {
                        reject(new Error('Failed to parse response'));
                    }
                } else {
                    try {
                        const errorData = JSON.parse(xhr.responseText);
                        reject(handleApiError({
                            status: xhr.status,
                            statusText: xhr.statusText,
                            json: () => Promise.resolve(errorData)
                        }, url));
                    } catch (error) {
                        reject(handleApiError({
                            status: xhr.status,
                            statusText: xhr.statusText
                        }, url));
                    }
                }
            };
            
            xhr.onerror = () => {
                reject(new Error('Network error during upload'));
            };
            
            xhr.send(formData);
        });
    }
    
    // Use fetch for simple uploads without progress tracking
    return fetch(url, config);
};

export { apiCall as default, isUserAuthenticated, isAuthenticatedEndpoint, isPublicEndpoint, uploadProfileImage };
